import React, { useMemo } from "react";

import { Button } from "@litres/ui-kit/button";
import { EArtType, type ArtShortSerializer } from "@litres-libs/types";
import { useAppSelector } from "@litres-core/store";
import { useTranslation } from "@litres-libs/i18n";

import { isBiblioDomain, getHrefForReading } from "@litres/utils";
import type { ISpotlightClickData } from "../../utils";
import { trackerSpotlightClickButton } from "../../utils";

import styles from "./SellableItemActionBlock.module.scss";

export const SellableItemActionBlock = (props: {
  art: ArtShortSerializer;
  eventData: ISpotlightClickData;
}): JSX.Element => {
  const { art, eventData } = props;
  const {
    my_art_status,
    url,
    art_type,
    is_auto_speech_gift,
    availability,
    art_subscription_status_for_user,
  } = art;

  const { t } = useTranslation();

  const user = useAppSelector(state => state.foundationUser);
  const clientHost = useAppSelector(
    state => state.browser.headers["client-host"]
  );
  const lang = useAppSelector(state => state.face.language || "ru");

  const isAcquired =
    my_art_status > 0 ||
    (!!user.subscription.is_active &&
      art_subscription_status_for_user === "active_subscription");

  const isAudioArt = [
    EArtType.audio,
    EArtType.podcast,
    EArtType.podcastEpisode,
  ].includes(art_type);

  const buttonTitle = useMemo((): string => {
    if (isAcquired) {
      return isAudioArt ? "Слушать" : "Читать";
    }
    return "Купить";
  }, []);

  const buttonHref = useMemo((): string => {
    if (!url) {
      return "";
    }

    if (isAcquired) {
      return getHrefForReading({
        art,
        user,
        isLibrary: isBiblioDomain(clientHost || ""),
        lang,
      });
    }

    return user.id === 0 ? `${url}#buy_now_noreg` : `${url}#buy_now_reg`;
  }, []);

  const onButtonClick = (
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ): void => {
    e.stopPropagation();
    e.preventDefault();
    trackerSpotlightClickButton(eventData, !isAcquired);
    window.location.href = buttonHref;
  };

  // Скрываем кнопки покупки для следующих кейсов:
  // 1) Предзаказ без даты продажи (availability === 2)
  // 2) Аудио арт, воспроизведенный роботом (авточтец) и идущий в подарок (is_auto_speech_gift=true)
  if (!isAcquired && (availability === 2 || is_auto_speech_gift)) {
    return <></>;
  }

  // Скрываем все кнопки для следующих кейсов:
  // 1) Библиотекарь
  // 2) Сборник подкастов
  // 3) Школьник (как на школьном домене, так и аккаунт с is_library_user=true)
  if (
    user.library_info.is_librarian ||
    art_type === EArtType.podcast ||
    user.library_info.is_school_domain ||
    user.library_info.is_library_user
  ) {
    return <></>;
  }

  return (
    <div className={styles.sellableItemActionBlockAB}>
      {isAcquired && (
        <div className={styles.button}>
          <Button
            mode="primary"
            size="small"
            onClick={onButtonClick}
            text={t(buttonTitle)}
          />
        </div>
      )}
    </div>
  );
};
